'use client'

import { useState, useEffect, useRef } from 'react'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { Expense, ExpenseCategory, ExpenseInput, ExpenseLineInput, JobcardListItem, ExpenseDetail } from '@garage/shared'
import {
  expenseInputSchema,
  EXPENSE_CATEGORIES,
  EXPENSE_CATEGORY_LABELS,
  parseMoneyToMinor,
  formatMinor,
  parseQuantityToMilli,
  formatMilli,
  expenseLineTotalMinor,
  expenseLinesSubtotalMinor,
} from '@garage/shared'
import { Dialog, TextField, Segmented, Autocomplete, Button, useSnackbar, IconButton, DeleteIcon } from '@/ui'
import { api, ApiError } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'

interface ExpenseFormDialogProps {
  open: boolean
  expense?: ExpenseDetail | Expense
  onClose: () => void
}

interface LineRow {
  key: string
  description: string
  quantityStr: string
  unitPriceStr: string
}

interface FormState {
  category: ExpenseCategory
  date: string
  invoiceNumber: string
  supplierName: string
  supplierTrn: string
  amountBeforeVatStr: string
  vatStr: string
  jobcardId: string | null
  jobcardQuery: string
  notes: string | null
  lines: LineRow[]
}

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

function generateLineKey(): string {
  return `line-${Date.now()}-${Math.random()}`
}

export function ExpenseFormDialog({ open, expense, onClose }: ExpenseFormDialogProps) {
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const { t } = useTranslation()

  const isCreate = !expense
  const isEdit = !isCreate
  const expenseId = isEdit ? expense.id : null
  const seededRef = useRef(false)

  // Fetch the full detail when editing (to get lines)
  const { data: expenseDetail, isLoading: detailLoading } = useQuery({
    queryKey: qk.expense(expenseId ?? ''),
    queryFn: () => api.expenses.get(expenseId!),
    enabled: isEdit && open && !!expenseId,
  })

  const initialState = (): FormState => {
    const baseExpense = isEdit && expenseDetail ? expenseDetail : expense
    const lines = isEdit && expenseDetail ? expenseDetail.lines : []

    return {
      category: baseExpense?.category ?? 'parts',
      date: baseExpense?.date ?? today(),
      invoiceNumber: baseExpense?.invoiceNumber ?? '',
      supplierName: baseExpense?.supplierName ?? '',
      supplierTrn: baseExpense?.supplierTrn ?? '',
      amountBeforeVatStr: baseExpense ? String(baseExpense.amountBeforeVatMinor / 100) : '',
      vatStr: baseExpense ? String(baseExpense.vatMinor / 100) : '',
      jobcardId: baseExpense?.jobcardId ?? null,
      jobcardQuery: '',
      notes: baseExpense?.notes ?? null,
      lines: lines.map((line) => ({
        key: line.id,
        description: line.description,
        quantityStr: formatMilli(line.quantityMilli),
        unitPriceStr: String(line.unitPriceMinor / 100),
      })),
    }
  }

  const [formState, setFormState] = useState<FormState>(initialState)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (open) {
      seededRef.current = false
      setFormState(initialState())
      setFieldErrors({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Seed lines from detail query once it loads (only once per open)
  useEffect(() => {
    if (isEdit && expenseDetail && !seededRef.current) {
      seededRef.current = true
      setFormState((prev) => ({
        ...prev,
        lines: expenseDetail.lines.map((line) => ({
          key: line.id,
          description: line.description,
          quantityStr: formatMilli(line.quantityMilli),
          unitPriceStr: String(line.unitPriceMinor / 100),
        })),
      }))
    }
  }, [isEdit, expenseDetail])

  const mutation = useMutation({
    mutationFn: async (input: ExpenseInput) => {
      const result = isCreate
        ? api.expenses.create(input)
        : api.expenses.update(expenseId!, input)
      return result
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: qk.expenses() })
      queryClient.invalidateQueries({ queryKey: qk.expense(saved.id) })
      queryClient.invalidateQueries({ queryKey: ['expenses-vat-summary'] })
      snackbar.success(isCreate ? t('expenseForm.expenseAdded') : t('expenseForm.expenseUpdated'))
      onClose()
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setFieldErrors(error.fieldErrors)
        snackbar.error(error.message)
      } else {
        snackbar.error(t('common.errorOccurred'))
      }
    },
  })

  const hasTrn = formState.supplierTrn.trim() !== ''

  // Parse line rows, skip blank ones, collect errors
  const parseLines = (): { lines: ExpenseLineInput[]; errors: Record<string, string>; uiIndexes: number[] } => {
    const lines: ExpenseLineInput[] = []
    const errors: Record<string, string> = {}
    // Which on-screen row each parsed line came from, so schema errors (indexed
    // by parsed line) can be shown on the right row even when blank rows are skipped.
    const uiIndexes: number[] = []

    for (let i = 0; i < formState.lines.length; i++) {
      const row = formState.lines[i]!
      const description = row.description.trim()
      const quantityStr = row.quantityStr.trim()
      const unitPriceStr = row.unitPriceStr.trim()

      // Skip blank rows
      if (!description && !quantityStr && !unitPriceStr) continue

      // Validate quantity
      if (!quantityStr) {
        errors[`lines.${i}.quantityMilli`] = t('expenseForm.quantityRequired')
        continue
      }
      const quantityMilli = parseQuantityToMilli(quantityStr)
      if (quantityMilli === null || quantityMilli <= 0) {
        errors[`lines.${i}.quantityMilli`] = t('expenseForm.invalidQuantity')
        continue
      }

      // Validate unit price
      if (!unitPriceStr) {
        errors[`lines.${i}.unitPriceMinor`] = t('expenseForm.unitPriceRequired')
        continue
      }
      const unitPriceMinor = parseMoneyToMinor(unitPriceStr)
      if (unitPriceMinor === null || unitPriceMinor < 0) {
        errors[`lines.${i}.unitPriceMinor`] = t('expenseForm.invalidUnitPrice')
        continue
      }

      lines.push({
        description,
        quantityMilli,
        unitPriceMinor,
      })
      uiIndexes.push(i)
    }

    return { lines, errors, uiIndexes }
  }

  // Compute subtotal from parsed lines
  const { lines: parsedLines } = parseLines()
  const linesSubtotalMinor = expenseLinesSubtotalMinor(parsedLines)
  const hasLines = parsedLines.length > 0

  // When there are lines, the before-VAT is derived from them
  const derivedBeforeVatMinor = hasLines ? linesSubtotalMinor : null
  const displayBeforeVatMinor = derivedBeforeVatMinor ?? (parseMoneyToMinor(formState.amountBeforeVatStr) ?? 0)

  const totalMinor = displayBeforeVatMinor + (hasTrn ? parseMoneyToMinor(formState.vatStr) ?? 0 : 0)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Parse lines and collect errors
    const { lines: submittedLines, errors: lineErrors, uiIndexes } = parseLines()

    // A half-filled row must be fixed, not silently dropped from the save.
    if (Object.keys(lineErrors).length > 0) {
      setFieldErrors(lineErrors)
      return
    }

    // Build the input
    const baseInput = {
      category: formState.category,
      date: formState.date,
      invoiceNumber: formState.invoiceNumber,
      supplierName: formState.supplierName,
      supplierTrn: formState.supplierTrn.trim() || null,
      amountBeforeVatMinor: derivedBeforeVatMinor ?? (parseMoneyToMinor(formState.amountBeforeVatStr) ?? 0),
      vatMinor: hasTrn ? (parseMoneyToMinor(formState.vatStr) ?? 0) : 0,
      jobcardId: formState.jobcardId,
      notes: formState.notes,
    }

    // Only include lines when appropriate:
    // - Create: include only if ≥1 row
    // - Edit: include the array ONLY after detail query has loaded
    const input: ExpenseInput = {
      ...baseInput,
      ...(isCreate
        ? submittedLines.length > 0
          ? { lines: submittedLines }
          : {}
        : detailLoading
          ? {} // Don't include lines; Save will be disabled anyway
          : { lines: submittedLines }),
    }

    const result = expenseInputSchema.safeParse(input)
    if (!result.success) {
      const errors: Record<string, string> = { ...lineErrors }
      result.error.issues.forEach((issue) => {
        const path = issue.path.map((p, n) => (n === 1 && issue.path[0] === 'lines' ? uiIndexes[p as number] ?? p : p))
        const key = path.join('.')
        if (!(key in errors)) {
          errors[key] = issue.message
        }
      })
      setFieldErrors(errors)
      return
    }

    setFieldErrors({})
    mutation.mutate(input)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isCreate ? t('accounting.addExpense') : t('accounting.editExpense')}
      size="sm"
      dismissible={false}
      actions={
        <div className="flex gap-3 justify-end">
          <Button variant="outlined" onClick={onClose} disabled={mutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="filled"
            onClick={handleSubmit}
            loading={mutation.isPending}
            disabled={isEdit && detailLoading}
          >
            {isCreate ? t('common.add') : t('common.save')}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Segmented
          label={t('accounting.category')}
          options={EXPENSE_CATEGORIES.map((c) => ({ value: c, label: t(`accounting.categories.${c}`, EXPENSE_CATEGORY_LABELS[c]) }))}
          value={formState.category}
          onChange={(category) => setFormState((s) => ({ ...s, category: category as ExpenseCategory }))}
        />

        <TextField
          label={t('calendar.date')}
          type="date"
          value={formState.date}
          onChange={(v) => setFormState((s) => ({ ...s, date: v }))}
          error={fieldErrors.date}
          fullWidth
        />

        <TextField
          label={t('expenseForm.invoiceNumber')}
          value={formState.invoiceNumber}
          onChange={(v) => setFormState((s) => ({ ...s, invoiceNumber: v }))}
          error={fieldErrors.invoiceNumber}
          fullWidth
          autoFocus
        />

        <TextField
          label={t('accounting.supplier')}
          value={formState.supplierName}
          onChange={(v) => setFormState((s) => ({ ...s, supplierName: v }))}
          error={fieldErrors.supplierName}
          fullWidth
        />

        <TextField
          label={t('expenseForm.supplierTrnLabel')}
          value={formState.supplierTrn}
          onChange={(v) =>
            setFormState((s) => ({ ...s, supplierTrn: v, vatStr: v.trim() ? s.vatStr : '0' }))
          }
          error={fieldErrors.supplierTrn}
          fullWidth
        />

        <div className="grid grid-cols-2 gap-3">
          <TextField
            label={t('accounting.beforeVat')}
            value={
              hasLines
                ? String(linesSubtotalMinor / 100)
                : formState.amountBeforeVatStr
            }
            onChange={(v) => {
              if (!hasLines) setFormState((s) => ({ ...s, amountBeforeVatStr: v }))
            }}
            error={fieldErrors.amountBeforeVatMinor}
            numeric
            disabled={hasLines}
            supportingText={hasLines ? t('expenseForm.fromBreakdown') : undefined}
            fullWidth
          />
          <TextField
            label={t('accounting.vat')}
            value={hasTrn ? formState.vatStr : '0'}
            onChange={(v) => setFormState((s) => ({ ...s, vatStr: v }))}
            error={fieldErrors.vatMinor}
            numeric
            disabled={!hasTrn}
            supportingText={!hasTrn ? t('expenseForm.noTrnCannotChargeVat') : undefined}
            fullWidth
          />
        </div>

        <div className="flex items-baseline justify-between text-body-sm text-on-surface-variant">
          <span>{t('dashboard.columns.total')}</span>
          <span className="tabular text-on-surface">{formatMinor(totalMinor, { decimals: 2 })}</span>
        </div>

        {/* Breakdown section: editable line items */}
        <div className="space-y-2 pt-2">
          <h3 className="text-label-md text-on-surface">{t('accounting.breakdown')}</h3>
          {formState.lines.length > 0 && (
            <div className="space-y-2">
              {formState.lines.map((row, index) => {
                const quantityMilli = parseQuantityToMilli(row.quantityStr.trim())
                const unitPriceMinor = parseMoneyToMinor(row.unitPriceStr.trim())
                const lineTotalMinor =
                  quantityMilli !== null && unitPriceMinor !== null
                    ? expenseLineTotalMinor({ quantityMilli, unitPriceMinor })
                    : 0

                return (
                  <div key={row.key} className="space-y-2 p-3 rounded-md bg-surface-low border border-outline-variant">
                    <TextField
                      label={t('common.description')}
                      value={row.description}
                      onChange={(v) =>
                        setFormState((s) => ({
                          ...s,
                          lines: s.lines.map((l, i) =>
                            i === index ? { ...l, description: v } : l
                          ),
                        }))
                      }
                      error={fieldErrors[`lines.${index}.description`]}
                      fullWidth
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <TextField
                        label={t('common.quantity')}
                        value={row.quantityStr}
                        onChange={(v) =>
                          setFormState((s) => ({
                            ...s,
                            lines: s.lines.map((l, i) =>
                              i === index ? { ...l, quantityStr: v } : l
                            ),
                          }))
                        }
                        error={fieldErrors[`lines.${index}.quantityMilli`]}
                        numeric
                        fullWidth
                      />
                      <TextField
                        label={t('accounting.unitPrice')}
                        value={row.unitPriceStr}
                        onChange={(v) =>
                          setFormState((s) => ({
                            ...s,
                            lines: s.lines.map((l, i) =>
                              i === index ? { ...l, unitPriceStr: v } : l
                            ),
                          }))
                        }
                        error={fieldErrors[`lines.${index}.unitPriceMinor`]}
                        numeric
                        fullWidth
                      />
                      <div className="flex flex-col gap-1">
                        <label className="text-label-sm text-on-surface-variant">{t('dashboard.columns.total')}</label>
                        <div className="h-10 flex items-center text-body-md tabular text-on-surface font-medium">
                          {formatMinor(lineTotalMinor, { decimals: 2 })}
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <IconButton
                        icon={<DeleteIcon className="w-5 h-5" />}
                        label={t('common.remove')}
                        variant="danger"
                        onClick={() =>
                          setFormState((s) => ({
                            ...s,
                            lines: s.lines.filter((_, i) => i !== index),
                          }))
                        }
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
          <Button
            variant="text"
            onClick={() =>
              setFormState((s) => ({
                ...s,
                lines: [
                  ...s.lines,
                  {
                    key: generateLineKey(),
                    description: '',
                    quantityStr: '',
                    unitPriceStr: '',
                  },
                ],
              }))
            }
          >
            {t('expenseForm.addLine')}
          </Button>
        </div>

        {formState.jobcardId ? (
          <div className="flex items-center justify-between gap-3 rounded-md border border-outline-variant px-3 py-2">
            <div className="text-body-sm text-on-surface">
              {t('expenseForm.linkedToJobcard')} <span className="ref">{formState.jobcardQuery || formState.jobcardId}</span>
            </div>
            <Button
              variant="text"
              size="sm"
              onClick={() => setFormState((s) => ({ ...s, jobcardId: null, jobcardQuery: '' }))}
            >
              {t('expenseForm.unlink')}
            </Button>
          </div>
        ) : (
          <Autocomplete
            label={t('expenseForm.linkToJobcardOptional')}
            value={formState.jobcardQuery}
            onChange={(text) => setFormState((s) => ({ ...s, jobcardQuery: text }))}
            onSelect={(jc: JobcardListItem) =>
              setFormState((s) => ({ ...s, jobcardId: jc.id, jobcardQuery: jc.number }))
            }
            fetchOptions={async (query) => {
              const result = await api.jobcards.list({ q: query, page: 1, pageSize: 10 })
              return result.items
            }}
            getOptionLabel={(jc) => jc.number}
            getOptionKey={(jc) => jc.id}
            renderOption={(jc) => (
              <div>
                <div className="font-medium ref">{jc.number}</div>
                <div className="text-xs text-on-surface-variant">
                  {jc.clientName || t('common.noCustomerYet')} · {jc.carLabel}
                </div>
              </div>
            )}
            placeholder={t('expenseForm.searchJobcardPlaceholder')}
            supportingText={t('expenseForm.linkToJobcardHelp')}
          />
        )}

        <TextField
          label={t('clientDetail.notes')}
          value={formState.notes ?? ''}
          onChange={(v) => setFormState((s) => ({ ...s, notes: v || null }))}
          error={fieldErrors.notes}
          multiline
          rows={2}
          fullWidth
        />
      </form>
    </Dialog>
  )
}
